import React from 'react';
import { Box, type BoxProps } from '@mui/material';

export type SurfaceProps = BoxProps & {
  /**
   * `raised` — the standard card: toolbars, panels, stat cards.
   * `sunken` — a recessed area inside a card, e.g. a grouped block or a
   * footer strip; no shadow.
   */
  variant?: 'raised' | 'sunken';
};

/**
 * The one card. Anything that needs a box around it — a toolbar, a chart
 * panel, a stat card — uses this, so every box on every page has the same
 * background, hairline border, corner radius and lift.
 *
 * `sx` is for layout only (display, padding, gap, position). Colour, border,
 * radius and shadow come from here; override them and the page drifts from
 * the rest of the product.
 *
 * Not every block needs a card. Group related things inside one Surface with
 * spacing and hairlines rather than nesting cards within cards.
 */
export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { variant = 'raised', sx, children, ...rest },
  ref,
) {
  return (
    <Box
      ref={ref}
      {...rest}
      sx={[
        (t) => ({
          minWidth: 0,
          bgcolor: variant === 'sunken' ? t.tokens.surface.sunken : t.tokens.surface.raised,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.md}px`,
          boxShadow: variant === 'raised' ? t.tokens.shadow.xs : 'none',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
});
